import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {WidgetData} from '../../../../../../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../../../../../../core/shared/widget/service/widget.factory';
import {WidgetDataParam} from '../../../../../../../../core/shared/widget/widget-data-param';
import {FilterCriteria} from '../../../../../../../../core/shared/filter/api/filter.criteria';
import {Sorting} from '../../../../../../../../core/shared/misc/sorting';
import {ObjectRequestList} from '../../../../../../../../core/shared/dto/object/request/object-request-list';
import {QlQueryDto} from '../../../../../../../../core/shared/ql/data/dto/ql-query.dto';
import {ObjectIdentifierData} from '../../../../../../../../core/shared/basic/object-identifier-data';
import {ObjectIdentifier} from '../../../../../../../../core/shared/basic/object-identifier';
import {MvsCoreService} from '../../../../../../../../core/shared/service/mvs-core.service';
import {DtoList} from '../../../../../../../../core/shared/dto/dto.list';
import {buildCwCaseTypeProductBiproTableWidget} from '../../../../../../widget/cw-case-type-product-bipro.widget';
// [CW-PERSON-REFACTOR] Typed read of the product/role configuration link — replaces the `any` cast
// that used to hide the caseTypePersonDtoId lookup below.
import {
    CwCaseTypeProductPersonDto
} from '../../../../../../model/dto/entity/cw-case-type-product-person.dto';
import {
    CwFkPreset,
    cwPersistTabParam,
    cwReadTabParam,
    cwWidgetToolbarCreate
} from '../../shared/cw-case-type-widget.util';
// [CW-IOT-DUAL-ANCHOR] Discriminator of the two row kinds of cw.CwCaseTypeProductObjectFieldIotField.
import {
    CwFieldMappingKindEnum
} from '../../../../../../model/dto/enum/config/cw-field-mapping-kind.enum';
import {
    CwFieldMappingBehaviourEnum
} from '../../../../../../model/dto/enum/config/cw-field-mapping-behaviour.enum';
// [CW-LOCAL-CASES] Betriebsmodus der Produktzeile — Feldsteuerung des Basis-Formulars
import {DtoDetail} from '../../../../../../../../core/shared/dto/dto.detail';
import {
    ObjectChangeInformation,
    ObjectChangeInformationActionEnum
} from '../../../../../../../../core/shared/logic/object-change-information';
import {
    MvsFormControlOverwrite
} from '../../../../../../../../core/shared/logic/form/logic/mvs-form-control-overwrite';
import {
    MvsFormFieldAccessEnum
} from '../../../../../../../../core/shared/logic/form/logic/mvs-form-field-access.enum';
import {
    CwCaseTypeProductModeEnum
} from '../../../../../../model/dto/enum/config/cw-case-type-product-mode.enum';
import {
    CW_CASE_TYPE_PRODUCT_MODE_LABELS
} from '../../../../../../model/dto/enum/config/cw-case-type-product-mode-labels.const';

/**
 * [CW-LOCAL-CASES / Phase A] Form field ids of the four steered attributes of
 * {@code cw.CwCaseTypeProduct}.
 *
 * KEY SPACE: the id is the one the backend form generator produces, i.e. the DTO attribute name —
 * a foreign key is addressed as {@code xxxDtoId}, never as {@code xxx}. Same key space as
 * {@code MvsFormControlOverwrite.addField()}.
 */
const CW_PRODUCT_FIELD_PRODUCT = 'productDtoId';
const CW_PRODUCT_FIELD_PARTNER = 'partnerDtoId';
const CW_PRODUCT_FIELD_CONTRACT_TYPE = 'contractTypeDtoId';
const CW_PRODUCT_FIELD_NAME = 'name';

/**
 * What one Betriebsmodus demands of the three classification foreign keys, and of the name.
 * {@code true} = the mode REQUIRES the field; {@code false} = the mode FORBIDS it (it has to stay
 * empty). This is the frontend mirror of the backend {@code CwCaseTypeProductValidator} matrix —
 * the two must say the same thing, or the screen offers a row the server then refuses.
 */
interface CwProductModeRule {
    product: boolean;
    partner: boolean;
    contractType: boolean;
    /** {@code name} is mandatory in every mode except PRODUCT, where it is not checked at all. */
    nameRequired: boolean;
}

/**
 * [CW-LOCAL-CASES / Phase A] The Betriebsmodus matrix, requirement §1.6.
 *
 * | Modus | Pflicht | muss leer sein |
 * |---|---|---|
 * | PRODUCT | product | partner, contractType |
 * | PARTNER | partner, name | product, contractType |
 * | PARTNER_CONTRACT_TYPE | partner, contractType, name | product |
 * | UNIVERSAL | name | product, partner, contractType |
 */
const CW_PRODUCT_MODE_RULES: Record<CwCaseTypeProductModeEnum, CwProductModeRule> = {
    [CwCaseTypeProductModeEnum.PRODUCT]:
        {product: true, partner: false, contractType: false, nameRequired: false},
    [CwCaseTypeProductModeEnum.PARTNER]:
        {product: false, partner: true, contractType: false, nameRequired: true},
    [CwCaseTypeProductModeEnum.PARTNER_CONTRACT_TYPE]:
        {product: false, partner: true, contractType: true, nameRequired: true},
    [CwCaseTypeProductModeEnum.UNIVERSAL]:
        {product: false, partner: false, contractType: false, nameRequired: true},
};

/**
 * Widget alias of the left product list. Extracted to a constant because the SAME alias has to be
 * emitted on every rebuild — it keys the persisted widget user state, and a second literal is how
 * that silently drifts.
 */
const CW_PRODUCT_LIST_WIDGET_ALIAS = 'cw.CwCaseTypeProduct.widget.basic.new';

/**
 * One entry of the product list's QL result, in the QL key space
 * ({@code <queryAlias>.<attribute>}). Only the keys this component actually reads are declared; the
 * query selects every column of the entity, so the index signature carries the rest.
 */
type CwProductQlRow = {
    'e.id'?: number;
    'e.mode'?: unknown;
    'e.name'?: string;
    'e.productId'?: number;
    'e.partnerId'?: number;
    'e.contractTypeId'?: number;
    'prod.name'?: string;
    'part.name'?: string;
    'ctyp.name'?: string;
} & { [key: string]: unknown };

/**
 * The persisted classification of one product line, already lifted out of whichever key space it
 * arrived in. {@code mode} stays {@code unknown} because the two sources deliver it differently (a
 * QL row an ordinal, a form group sometimes a string) — {@code normalizeMode} is the single point
 * that resolves it.
 */
interface CwProductClassification {
    mode: unknown;
    productId: number;
    partnerId: number;
    contractTypeId: number;
    productName: string;
    partnerName: string;
    contractTypeName: string;
}

/** One line of the requirement legend rendered above the Basis form. */
export interface CwProductModeRequirementRow {
    label: string;
    /** 'required' → Pflichtfeld, 'forbidden' → muss leer bleiben. */
    state: 'required' | 'forbidden';
    /** Set when the SAVED row still carries a value the selected mode forbids. */
    conflict: boolean;
}

/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: CrudIntegration
 * Reason: "Produkte" navigation point of the CwCaseType standard view — a selectable product
 *   list on the left; after selection the selected CwCaseTypeProduct is modelled in lazy tabs
 *   (Basis data+edit, Bipro, Felder, Tarifvarianten, Basisdeckungen, Vertragslaufzeiten,
 *   Elementarprodukte, Personen, Objekte, Fragen, Vorherige Verträge) with the nested
 *   coverage/person/object/question drills. [CW-PERSON-REFACTOR] The Personen drill is Person →
 *   Elementarprodukte → Felder; it has no Person → Felder level, see handleProductPersonSelect().
 *   Inactive tabs are not rendered (@if on the active tab; lazy widget init, cached).
 */
@Component({
    selector: 'cw-case-type-products',
    templateUrl: './cw-case-type-products.component.html',
    styleUrls: ['./cw-case-type-products.component.scss'],
    standalone: false
})
export class CwCaseTypeProductsComponent implements OnInit, OnChanges {

    @Input() caseTypeId: number;

    /** Left column: selectable CwCaseTypeProduct list (category/selectable pattern). */
    productListWidget: WidgetData;
    selectedProductId: number;

    /** Right column: the active product tab; drives the single-tab @if guards. */
    productActiveTab: string = 'basis';

    private readonly validProductTabs = ['basis', 'bipro', 'fields', 'tariffs', 'baseCoverages', 'durations',
        'coverages', 'persons', 'objects', 'questions', 'formerContracts', 'businessTypes',
        'partnerRestricts'];

    // --- Basis (data + edit) ---
    productBasicDataWidget: WidgetData;
    productBasicEditWidget: WidgetData;
    editProductBasic: boolean = false;

    // --- [CW-LOCAL-CASES] Betriebsmodus der ausgewählten Produktzeile ---

    /** Betriebsmodus as PERSISTED on the selected row. Defaults to PRODUCT for a row without one. */
    selectedProductMode: CwCaseTypeProductModeEnum = CwCaseTypeProductModeEnum.PRODUCT;

    /**
     * Betriebsmodus currently CHOSEN in the open Basis form — equal to {@link selectedProductMode}
     * until the user picks a different one, and again after the save round trip. Everything the
     * legend says is said about THIS value, because that is the configuration the user is building.
     */
    productFormMode: CwCaseTypeProductModeEnum = CwCaseTypeProductModeEnum.PRODUCT;

    /**
     * The §1.6 legend rendered above the Basis form, for the Betriebsmodus currently chosen in it.
     * Held as a FIELD and recomputed on the three events that can change it (selection, mode change,
     * save) rather than as a getter — a getter would allocate a new array on every change-detection
     * run and defeat the {@code @for} track.
     */
    productModeRequirements: CwProductModeRequirementRow[] = [];

    /**
     * The saved {@code name} of the selected line — the ONE column the left list renders. Kept so a
     * save can rebuild that list only when the value it displays actually changed.
     */
    private selectedProductName: string;

    /** Resolved names of the persisted classification, for the summary band. */
    selectedProductProductName: string;
    selectedProductPartnerName: string;
    selectedProductContractTypeName: string;

    /** Persisted classification ids — they decide whether a forbidden field still has to be shown. */
    private selectedProductProductId: number;
    private selectedProductPartnerId: number;
    private selectedProductContractTypeId: number;

    /**
     * THE overwrite instance of the Basis edit form. Held as ONE object for the life of the widget
     * and MUTATED in place, never replaced.
     *
     * <h3>Why mutation and not a new instance</h3>
     * {@code MvsCrudObjectComponent} hands this very reference to the dynamically created generic
     * CRUD component, which passes it to {@code MvsFormControlService.buildFormGroup()} on EVERY
     * form build — including the one after a backend form-template refresh, which is the only
     * rebuild that keeps the values the user has already typed. Assigning a NEW object instead would
     * change the {@code mvs-crud-object} input, and that input change destroys and re-creates the
     * whole form, which re-fetches the row from the server and would throw away the mode the user
     * just picked.
     *
     * <h3>Why every build stamps ALL four fields</h3>
     * {@code buildFormGroup} writes {@code formField.access} onto {@code MvsFormFieldDto} instances
     * that are REUSED across rebuilds, and — unlike label, input control and value list — access has
     * NO restore branch. A field hidden by one build therefore STAYS hidden when the next build
     * simply omits its entry. {@link stampProductBasicOverwrite} consequently stamps product,
     * partner, contractType AND name explicitly on every single call; leaving one out because it is
     * "unchanged" is the measured defect, not an optimisation.
     */
    private productBasicOverwrite: MvsFormControlOverwrite;

    // --- Bipro ---
    productBiproWidget: WidgetData;

    // --- Felder ---
    productFieldWidget: WidgetData;

    // --- Tarifvarianten (CwCaseTypeProductTariff) ---
    productTariffWidget: WidgetData;

    // --- Basisdeckungen (CwCaseTypeProductBaseCoverage) ---
    productBaseCoverageWidget: WidgetData;

    // --- Vertragslaufzeiten (CwCaseTypeProductDuration) ---
    productDurationWidget: WidgetData;

    // --- Elementarprodukte (ProductCoverage → CoverageField + CoverageAllowance) ---
    productCoverageWidget: WidgetData;
    productCoverageFieldWidget: WidgetData;
    /** Allowance rows of the selected coverage — the tariff/base-coverage dependency of ONE component. */
    productCoverageAllowanceWidget: WidgetData;
    selectedProductCoverageId: number;

    // --- Personen (ProductPerson → ProductPersonCoverage → PersonCoverageField) ---
    // [CW-PERSON-REFACTOR] There is no field table on the ProductPerson level any more. The pool that
    // used to hang off the product/role link (cw.CwCaseTypeProductPersonField) was deleted with the
    // refactor; the surviving pool is cw.CwCaseTypePersonField and it is maintained one level up, in
    // Übersicht > Personen. See handleProductPersonSelect() for the full reasoning.
    productPersonWidget: WidgetData;
    productPersonCoverageWidget: WidgetData;
    productPersonCoverageFieldWidget: WidgetData;
    selectedProductPersonId: number;
    selectedProductPersonCaseTypePersonId: number;
    selectedProductPersonCoverageId: number;

    /**
     * Allowance rows of the selected person line — the third leg of the allowance triad next to
     * {@link productCoverageAllowanceWidget} and {@link productObjectAllowanceWidget}. Its FK is
     * {@code caseTypeProductPerson}, i.e. the PRODUCT-SCOPED person row, not the case-level role.
     */
    productPersonAllowanceWidget: WidgetData;

    /**
     * Pflichtangaben pool of the selected person line (cw.CwCaseTypeProductPersonReqField). Its FK is
     * called {@code type} — the GT-field quartet naming — and NOT {@code productPerson}.
     */
    productPersonReqFieldWidget: WidgetData;

    /** Bezugsrechte of the selected person line — selectable master of its own field pool. */
    productBeneficiaryWidget: WidgetData;
    productBeneficiaryFieldWidget: WidgetData;
    selectedProductBeneficiaryId: number;

    // --- Objekte (ProductObject → ObjectField → IotField + ProductObjectCoverage → ObjectCoverageField) ---
    productObjectWidget: WidgetData;
    productObjectFieldWidget: WidgetData;
    productObjectCoverageWidget: WidgetData;
    productObjectCoverageFieldWidget: WidgetData;
    selectedProductObjectId: number;
    selectedProductObjectCoverageId: number;

    /** Allowance rows of the selected object line — the object leg of the allowance triad. */
    productObjectAllowanceWidget: WidgetData;

    /**
     * [CW-IOT-DUAL-ANCHOR] The TARGET_FIELD half of cw.CwCaseTypeProductObjectFieldIotField: the source-field
     * mappings of the SELECTED object field row (FK {@code caseTypeProductObjectField}).
     */
    productObjectFieldIotFieldWidget: WidgetData;
    selectedProductObjectFieldId: number;

    /**
     * [CW-IOT-DUAL-ANCHOR] The DEFINITION half of the SAME entity, anchored one level up on the object
     * itself (FK {@code caseTypeProductObject}). Such a row occupies no product field at all: it declares
     * the {@code ci.InsurableObjectTypeField} as needed and publishes its value under its own alias. The
     * requirement load path filters on {@code mappingKind == DEFINITION}, so these rows are load-bearing
     * and need a surface of their own — the field-scoped table above can never reach them.
     */
    productObjectDefinitionIotFieldWidget: WidgetData;

    // --- Fragen (ProductQuestionSection → ProductQuestion) ---
    productQuestionSectionWidget: WidgetData;
    productQuestionWidget: WidgetData;
    selectedProductQuestionSectionId: number;

    // --- Vorherige Verträge (product level) ---
    productFormerContractTypeWidget: WidgetData;
    productFormerProductWidget: WidgetData;

    // --- [CW-PRODUCT-BUSINESS-TYPE] Geschäftsarten (product level) ---
    productBusinessTypeWidget: WidgetData;
    // [cw Vorvertrag-Erweiterung / R9] Geschäftsart → Felder (CwCaseTypeProductBusinessTypeField, FK type)
    productBusinessTypeFieldWidget: WidgetData;
    selectedProductBusinessTypeId: number;
    // [R9 audit fix] Basis (data + edit) of the selected Geschäftsart row — same pattern as Produkte > Basis.
    // Without it the row's own attributes (Vorvertrag-Kündigung, Prüfmodus, Kündigungsart/-grund,
    // Vertragsbasis, name, active …) would only be editable at creation.
    productBusinessTypeDataWidget: WidgetData;
    productBusinessTypeEditWidget: WidgetData;
    editProductBusinessType: boolean = false;
    /** Geschäftsart value of the selected row as shown in the list — decides whether a save must relabel it. */
    private selectedProductBusinessTypeValue: any = null;
    /** Optional Bezeichnung of the selected row, shown next to the Geschäftsart in the detail header. */
    selectedProductBusinessTypeName: string = null;

    // --- [CW-PRODUCT-PARTNER-RESTRICT] Gesellschaften/Vertragstypen (product level) ---
    productPartnerRestrictWidget: WidgetData;

    constructor(
        protected coreService: MvsCoreService,
        private route: ActivatedRoute,
        private router: Router) {
    }

    ngOnInit(): void {
        this.buildProductList();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['caseTypeId'] && !changes['caseTypeId'].firstChange) {
            this.productListWidget = null;
            this.clearProductSelection();
            this.buildProductList();
        }
    }

    private buildProductList(): void {
        /**
         * [CW-LOCAL-CASES] The left product list, on a hand-written QL query with THREE NAMED LEFT
         * JOINS instead of the factory default.
         *
         * <h3>Why named joins and not resolve_fkey_texts</h3>
         * {@code resolve_fkey_texts} does not un-prefix anything: it appends one synthetic join per
         * resolvable N:1 relation under the POSITIONAL alias {@code fkeyt_e_<N>}
         * ({@code QlDpEntityJdbcProviderImplResolver}, {@code PREFIX_AS}), so the name arrives as
         * {@code fkeyt_e_<N>.name} and N shifts whenever a relation is added, removed or becomes
         * resolvable. That index cannot be derived from the entity source; it has to be pinned
         * against a running backend. Naming the three joins we actually need gives stable keys
         * ({@code prod.name}, {@code part.name}, {@code ctyp.name}) and joins three relations
         * instead of every resolvable one.
         *
         * <h3>The start carries no field list on purpose</h3>
         * Omitting {@code fields} keeps every column of the entity, exactly as the previous factory
         * call did — including {@code e.id} and {@code e.name}, which the selectable widget needs,
         * and the raw foreign keys {@code e.productId} / {@code e.partnerId} /
         * {@code e.contractTypeId}. Narrowing it would be a second, silent way to lose a column.
         *
         * <h3>The alias is deliberately unchanged</h3>
         * It keys the persisted widget user state, so a new one would silently reset every admin's
         * settings for this list.
         */
        const qlQuery: QlQueryDto = {
            name: CW_PRODUCT_LIST_WIDGET_ALIAS,
            type: 'view',
            start: {
                name: 'cw.CwCaseTypeProduct',
                as: 'e',
                type: 'entity',
                joins: [
                    {
                        // FK product → cm.Product. Empty in every mode except PRODUCT.
                        type: 'entity', name: 'product', as: 'prod', joinType: 'left',
                        fields: [{name: 'id'}, {name: 'name'}],
                    },
                    {
                        // FK partner → cm.Partner. Carried by PARTNER and PARTNER_CONTRACT_TYPE.
                        type: 'entity', name: 'partner', as: 'part', joinType: 'left',
                        fields: [{name: 'id'}, {name: 'name'}],
                    },
                    {
                        // FK contractType → cm.ContractType. Carried by PARTNER_CONTRACT_TYPE.
                        type: 'entity', name: 'contractType', as: 'ctyp', joinType: 'left',
                        fields: [{name: 'id'}, {name: 'name'}],
                    },
                ],
            },
            fixedFilters: [FilterCriteria.create('e.caseTypeId', FilterCriteria.cOperatorEqual, this.caseTypeId)],
            sortings: [],
        };

        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeProduct selectable list scoped to the case type (Produkte left column),
         *   with the Produkt/Gesellschaft/Vertragstyp names joined under stable aliases so the
         *   classification band and the Betriebsmodus field steering can read them off the selected
         *   row without a second request.
         */
        this.productListWidget = WidgetFactory.createWidgetListQl(
            CW_PRODUCT_LIST_WIDGET_ALIAS,
            'Produkte',
            'selectable',
            qlQuery,
            WidgetDataParam.create('attributeToDisplay', 'e.name')
        );
        this.productListWidget.noDataText = 'Keine Produkte vorhanden';
        this.productListWidget.functionCallbacks = cwWidgetToolbarCreate([{field: 'caseTypeDtoId', value: this.caseTypeId}]);
    }

    handleProductSelect(event: ObjectIdentifierData | ObjectIdentifier): void {
        const row = (event as ObjectIdentifierData)?.data as CwProductQlRow;
        const productId = row?.['e.id'];
        if (!productId) {
            return;
        }
        this.clearProductSelection();
        this.selectedProductId = productId;
        // [CW-LOCAL-CASES] Take the persisted classification straight off the selected QL row — no
        // extra request. The three names come from the named joins of buildProductList().
        this.applySelectedProductClassificationFromQlRow(row);
        this.selectedProductName = row?.['e.name'] ?? null;
        this.productActiveTab = cwReadTabParam(this.route, this.validProductTabs, 'basis');
        this.buildProductActiveTab();
    }

    /**
     * [CW-LOCAL-CASES] ADAPTER 1 of 2 — the QL row of the selected line.
     *
     * KEY SPACE, and it is not the DTO one: under {@code dataProvider: 'ql'} every result key is
     * {@code <queryAlias>.<attribute>} ({@code QlDataMetaMember.getFullAttributeName()}). An entity
     * column is therefore {@code e.<attribute>}, a foreign key is the RAW id {@code e.<rel>Id} — the
     * same space the fixed filter {@code e.caseTypeId} above uses — and a joined name is
     * {@code <joinAlias>.name}. There is no {@code e.<rel>DtoId} and no {@code e.<rel>DtoName} here:
     * those belong to {@code dataProvider: 'entity'}, whose form is built from DtoMeta, and reading
     * them off a QL row yields {@code undefined} with no error at all.
     *
     * @param row one entry of the selectable list's QL result
     */
    private applySelectedProductClassificationFromQlRow(row: CwProductQlRow): void {
        this.setSelectedProductClassification({
            mode: row?.['e.mode'],
            productId: row?.['e.productId'] ?? null,
            partnerId: row?.['e.partnerId'] ?? null,
            contractTypeId: row?.['e.contractTypeId'] ?? null,
            productName: row?.['prod.name'] ?? null,
            partnerName: row?.['part.name'] ?? null,
            contractTypeName: row?.['ctyp.name'] ?? null,
        });
    }

    /**
     * [CW-LOCAL-CASES] ADAPTER 2 of 2 — the saved DTO handed back by the CRUD form.
     *
     * KEY SPACE: a {@code DtoDetail}, so foreign keys are {@code <rel>DtoId} / {@code <rel>DtoName}.
     * That IS the correct space here and the wrong one in {@link
     * #applySelectedProductClassificationFromQlRow} — which is exactly why both funnel into
     * {@link #setSelectedProductClassification} instead of assigning the fields themselves. Two
     * adapters, one target: a key-space mistake shows up as a broken adapter, not as two halves of
     * the component quietly disagreeing about what a line is bound to.
     */
    private applySelectedProductClassificationFromDto(dto: DtoDetail): void {
        this.setSelectedProductClassification({
            mode: dto?.['mode'],
            productId: dto?.['productDtoId'] ?? null,
            partnerId: dto?.['partnerDtoId'] ?? null,
            contractTypeId: dto?.['contractTypeDtoId'] ?? null,
            productName: dto?.['productDtoName'] ?? null,
            partnerName: dto?.['partnerDtoName'] ?? null,
            contractTypeName: dto?.['contractTypeDtoName'] ?? null,
        });
    }

    /**
     * [CW-LOCAL-CASES] The ONE place the persisted classification of the selected line is written.
     *
     * A line written before the Betriebsmodus existed answers with no {@code mode} at all; it is a
     * PRODUCT line by definition (business rule GR-3, and the migration backfills exactly that), so
     * an absent value falls back to PRODUCT rather than leaving the screen unsteered.
     */
    private setSelectedProductClassification(classification: CwProductClassification): void {
        this.selectedProductMode = this.normalizeMode(classification.mode);
        this.productFormMode = this.selectedProductMode;

        this.selectedProductProductId = classification.productId;
        this.selectedProductPartnerId = classification.partnerId;
        this.selectedProductContractTypeId = classification.contractTypeId;

        this.selectedProductProductName = classification.productName;
        this.selectedProductPartnerName = classification.partnerName;
        this.selectedProductContractTypeName = classification.contractTypeName;

        this.refreshProductModeRequirements();
    }

    /**
     * Coerces whatever the wire delivered for {@code mode} into a known enum member.
     *
     * The enum is persisted {@code @Enumerated(ORDINAL)} and travels as its ordinal, so the value
     * arrives as a number — but a form group hands it back as a string on some input controls.
     * Anything outside 0…3, and anything absent, is PRODUCT (see the caller's note on GR-3).
     */
    private normalizeMode(raw: unknown): CwCaseTypeProductModeEnum {
        const ordinal = Number(raw);
        if (!Number.isInteger(ordinal) || !(ordinal in CW_PRODUCT_MODE_RULES)) {
            return CwCaseTypeProductModeEnum.PRODUCT;
        }
        return ordinal as CwCaseTypeProductModeEnum;
    }

    /** German label of a Betriebsmodus — the same wording the backend carries in its UI metadata. */
    modeLabel(mode: CwCaseTypeProductModeEnum): string {
        return CW_CASE_TYPE_PRODUCT_MODE_LABELS[mode] ?? String(mode);
    }

    /**
     * [CW-LOCAL-CASES] The requirement legend of the Betriebsmodus currently chosen in the form —
     * exactly the §1.6 matrix, rendered so the configurator reads the same rule the server enforces.
     *
     * A row is marked as a CONFLICT when the mode forbids the field but the SAVED line still carries
     * a value in it. That is the state a mode switch produces, and it is the reason such a field
     * stays visible in the form: the value has to be cleared by hand before the line can be saved.
     */
    private refreshProductModeRequirements(): void {
        const rule = CW_PRODUCT_MODE_RULES[this.productFormMode];
        if (!rule) {
            this.productModeRequirements = [];
            return;
        }
        const rows: CwProductModeRequirementRow[] = [
            {
                label: 'Produkt',
                state: rule.product ? 'required' : 'forbidden',
                conflict: !rule.product && this.selectedProductProductId != null,
            },
            {
                label: 'Gesellschaft',
                state: rule.partner ? 'required' : 'forbidden',
                conflict: !rule.partner && this.selectedProductPartnerId != null,
            },
            {
                label: 'Vertragstyp',
                state: rule.contractType ? 'required' : 'forbidden',
                conflict: !rule.contractType && this.selectedProductContractTypeId != null,
            },
        ];
        // Die Bezeichnung erscheint nur, wo sie Pflicht ist. In PRODUCT wird sie vom Validator gar
        // nicht geprüft — sie dort als „muss leer bleiben" zu führen wäre schlicht falsch.
        if (rule.nameRequired) {
            rows.push({label: 'Bezeichnung', state: 'required', conflict: false});
        }
        this.productModeRequirements = rows;
    }

    /** Whether the form currently shows a Betriebsmodus that is not the saved one. */
    get productModeChangedInForm(): boolean {
        return this.productFormMode !== this.selectedProductMode;
    }

    onProductTabChange(tab: string | number): void {
        this.productActiveTab = String(tab);
        cwPersistTabParam(this.router, this.route, this.productActiveTab);
        this.buildProductActiveTab();
    }

    private buildProductActiveTab(): void {
        if (!this.selectedProductId) {
            return;
        }
        switch (this.productActiveTab) {
            case 'basis':
                this.buildProductBasis();
                break;
            case 'bipro':
                if (!this.productBiproWidget) {
                    this.productBiproWidget = buildCwCaseTypeProductBiproTableWidget(this.selectedProductId);
                }
                break;
            case 'fields':
                if (!this.productFieldWidget) {
                    /**
                     * AI: Status "in progress" | Type Widget | SubType Runtime
                     * Reason: CwCaseTypeProductField table (FK type) for the selected product (Produkte > Felder).
                     */
                    this.productFieldWidget = this.typeChild(
                        'cw.CwCaseTypeProductField.widget.basic.new',
                        'Felder', 'cw.CwCaseTypeProductField', 'Keine Felder vorhanden', this.selectedProductId
                    );
                }
                break;
            case 'tariffs':
                if (!this.productTariffWidget) {
                    /**
                     * AI: Status "in progress" | Type Widget | SubType Runtime
                     * Reason: CwCaseTypeProductTariff table (FK caseTypeProduct) for the selected product
                     *   (Produkte > Tarifvarianten) — the tariff variants of the cm product released for
                     *   this case type.
                     */
                    this.productTariffWidget = this.productTable(
                        'cw.CwCaseTypeProductTariff.widget.basic.new',
                        'Tarifvarianten', 'cw.CwCaseTypeProductTariff', 'Keine Tarifvarianten vorhanden'
                    );
                }
                break;
            case 'baseCoverages':
                if (!this.productBaseCoverageWidget) {
                    /**
                     * AI: Status "in progress" | Type Widget | SubType Runtime
                     * Reason: CwCaseTypeProductBaseCoverage table (FK caseTypeProduct) for the selected
                     *   product (Produkte > Basisdeckungen) — the base coverages of the cm product released
                     *   for this case type.
                     */
                    this.productBaseCoverageWidget = this.productTable(
                        'cw.CwCaseTypeProductBaseCoverage.widget.basic.new',
                        'Basisdeckungen', 'cw.CwCaseTypeProductBaseCoverage', 'Keine Basisdeckungen vorhanden'
                    );
                }
                break;
            case 'durations':
                if (!this.productDurationWidget) {
                    /**
                     * AI: Status "in progress" | Type Widget | SubType Runtime
                     * Reason: CwCaseTypeProductDuration table (FK caseTypeProduct) for the selected product
                     *   (Produkte > Vertragslaufzeiten) — the duration list of THIS product line, the
                     *   narrower sibling of the case-type-wide list in Übersicht > Vertragslaufzeiten.
                     */
                    // Placed next to Tarifvarianten/Basisdeckungen because all three are flat value lists
                    // the product line releases for selection; the drill tabs below start at Elementarprodukte.
                    // Note the precedence the empty state has to live with: as soon as ONE row exists here,
                    // the case type's list is ignored for this line (CwCaseDurationResolver) — an override,
                    // not an intersection.
                    this.productDurationWidget = this.productTable(
                        'cw.CwCaseTypeProductDuration.widget.basic.new',
                        'Vertragslaufzeiten', 'cw.CwCaseTypeProductDuration', 'Keine Vertragslaufzeiten vorhanden',
                        new Sorting('e.orderPriority', true)
                    );
                }
                break;
            case 'coverages':
                if (!this.productCoverageWidget) {
                    /**
                     * AI: Status "in progress" | Type Widget | SubType Runtime
                     * Reason: CwCaseTypeProductCoverage selectable master for the selected product (Produkte > Elementarprodukte).
                     */
                    this.productCoverageWidget = this.productSelectable(
                        'cw.CwCaseTypeProductCoverage.widget.basic.new',
                        'Elementarprodukte', 'cw.CwCaseTypeProductCoverage', 'Keine Elementarprodukte vorhanden'
                    );
                }
                break;
            case 'persons':
                if (!this.productPersonWidget) {
                    /**
                     * AI: Status "in progress" | Type Widget | SubType Runtime
                     * Reason: CwCaseTypeProductPerson selectable master for the selected product (Produkte > Personen).
                     */
                    // [CW-PERSON-REFACTOR] The MASTER survives the refactor unchanged — the entity is
                    // still there, it merely stopped being a GenericTemplateType. Only its Felder child
                    // table was removed (see handleProductPersonSelect()).
                    this.productPersonWidget = this.productSelectable(
                        'cw.CwCaseTypeProductPerson.widget.basic.new',
                        'Personen', 'cw.CwCaseTypeProductPerson', 'Keine Personen vorhanden'
                    );
                }
                break;
            case 'objects':
                if (!this.productObjectWidget) {
                    /**
                     * AI: Status "in progress" | Type Widget | SubType Runtime
                     * Reason: CwCaseTypeProductObject selectable master for the selected product (Produkte > Objekte).
                     */
                    this.productObjectWidget = this.productSelectable(
                        'cw.CwCaseTypeProductObject.widget.basic.new',
                        'Objekte', 'cw.CwCaseTypeProductObject', 'Keine Objekte vorhanden'
                    );
                }
                break;
            case 'questions':
                if (!this.productQuestionSectionWidget) {
                    /**
                     * AI: Status "in progress" | Type Widget | SubType Runtime
                     * Reason: CwCaseTypeProductQuestionSection selectable master for the selected product (Produkte > Fragen).
                     */
                    // This is the architecturally correct home for the product-side question family:
                    // the section hangs off caseTypeProduct, so the case-level Fragen tab would have to
                    // filter over a join axis it does not have. A newly created configuration entity
                    // without its own surface is a configuration entity nobody can configure.
                    this.productQuestionSectionWidget = this.productSelectable(
                        'cw.CwCaseTypeProductQuestionSection.widget.basic.new',
                        'Fragensektionen', 'cw.CwCaseTypeProductQuestionSection',
                        'Keine Fragensektionen vorhanden'
                    );
                }
                break;
            case 'formerContracts':
                if (!this.productFormerContractTypeWidget) {
                    /**
                     * AI: Status "in progress" | Type Widget | SubType Runtime
                     * Reason: CwCaseTypeProductFormerContractType table for the selected product (Produkte > Vorherige Verträge).
                     */
                    this.productFormerContractTypeWidget = this.productTable(
                        'cw.CwCaseTypeProductFormerContractType.widget.basic.new',
                        'Vorvertragstypen', 'cw.CwCaseTypeProductFormerContractType', 'Keine Vorvertragstypen vorhanden'
                    );
                    /**
                     * AI: Status "in progress" | Type Widget | SubType Runtime
                     * Reason: CwCaseTypeProductFormerProduct table for the selected product (Produkte > Vorherige Verträge).
                     */
                    this.productFormerProductWidget = this.productTable(
                        'cw.CwCaseTypeProductFormerProduct.widget.basic.new',
                        'Vorprodukte', 'cw.CwCaseTypeProductFormerProduct', 'Keine Vorprodukte vorhanden'
                    );
                }
                break;
            case 'businessTypes':
                if (!this.productBusinessTypeWidget) {
                    /**
                     * AI: Status "in progress" | Type Widget | SubType Runtime
                     * Reason: [CW-PRODUCT-BUSINESS-TYPE] CwCaseTypeProductBusinessType table (FK
                     *   caseTypeProduct) for the selected product (Produkte > Geschäftsarten) — the
                     *   Geschäftsarten THIS product line may be sold in.
                     */
                    // The product-level twin of the case-wide Geschäftsarten list (Übersicht >
                    // Geschäftsarten, cw.CwCaseTypeBusinessType). Both levels coexist and neither
                    // narrows the other: the case governs CwCase.businessType, this list governs
                    // CwCaseProduct.businessType, so a Verkaufsprodukt can be a Neuvertrag case with an
                    // Umdeckung line. It belongs HERE and not in the case-level tab because its FK is
                    // caseTypeProduct — and a configuration entity without its own surface is a
                    // configuration entity nobody can configure.
                    //
                    // [cw Vorvertrag-Erweiterung / R9] Master-detail like Elementarprodukte: the row is the
                    // generic-template TYPE of the Geschäftsart data, so selecting it opens its „Felder" on the
                    // right (handleProductBusinessTypeSelect). Labelled by the Geschäftsart itself — `name` is
                    // optional on this entity, the Geschäftsart never is.
                    this.buildProductBusinessTypeList();
                }
                break;
            case 'partnerRestricts':
                if (!this.productPartnerRestrictWidget) {
                    /**
                     * AI: Status "in progress" | Type Widget | SubType Runtime
                     * Reason: [CW-PRODUCT-PARTNER-RESTRICT] CwCaseTypeProductPartnerRestrict table (FK
                     *   caseTypeProduct) for the selected product (Produkte >
                     *   Gesellschaften/Vertragstypen) — the Gesellschaft/Vertragstyp/(Produkt)
                     *   combinations THIS product line may be sold with.
                     */
                    // The allow-list of the line, not a blocklist: each row whitelists ONE combination
                    // of Gesellschaft, Vertragstyp and — optionally — Produkt. An EMPTY list is the
                    // absence of a restriction, so a line nobody has restricted keeps offering
                    // everything. It belongs HERE and not on the case level because its FK is
                    // caseTypeProduct — and a configuration entity without its own surface is a
                    // configuration entity nobody can configure. The runtime narrowing itself is
                    // metadata-driven and delivered entirely by the backend form of the LOCAL_PRODUCT
                    // step; there is nothing to do for it on the frontend side.
                    this.productPartnerRestrictWidget = this.productTable(
                        'cw.CwCaseTypeProductPartnerRestrict.widget.basic.new',
                        'Gesellschaften/Vertragstypen', 'cw.CwCaseTypeProductPartnerRestrict',
                        'Keine Einschränkung konfiguriert — alle Gesellschaften und Vertragstypen erlaubt'
                    );
                }
                break;
        }
    }

    private buildProductBasis(): void {
        if (this.productBasicDataWidget) {
            return;
        }
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeProduct base-data read widget (Produkte > Basis).
         */
        this.productBasicDataWidget = WidgetFactory.createWidgetEntityData(
            'cw.CwCaseTypeProduct.widget.data',
            'Produktdaten',
            'cw.CwCaseTypeProduct',
            this.selectedProductId
        );
    }

    toggleProductBasicEdit(): void {
        this.editProductBasic = !this.editProductBasic;
        if (this.editProductBasic && !this.productBasicEditWidget) {
            this.productBasicEditWidget = this.buildProductBasicEditWidget();
        }
    }

    /**
     * [CW-LOCAL-CASES] Builds the editable Basis form of the selected product line, steered by the
     * Betriebsmodus.
     *
     * The overwrite object is created ONCE here and then only ever mutated (see
     * {@link productBasicOverwrite}); the widget carries it as the {@code formControlOverwrite}
     * param, which {@code MvsWidgetObjectComponent} forwards by reference to the CRUD object.
     */
    private buildProductBasicEditWidget(): WidgetData {
        this.productBasicOverwrite = new MvsFormControlOverwrite();
        this.productFormMode = this.selectedProductMode;
        this.stampProductBasicOverwrite(this.productFormMode);

        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeProduct editable object form (Produkte > Basis, edit toggle), with the
         *   Betriebsmodus field steering of requirement §1.6 applied through a form-control
         *   overwrite — never through a hand-built Angular form.
         */
        return WidgetFactory.createWidgetObject(
            'cw.CwCaseTypeProduct.widget.object.edit',
            'Produktdaten bearbeiten',
            'cw.CwCaseTypeProduct',
            this.selectedProductId,
            WidgetDataParam.create('formControlOverwrite', this.productBasicOverwrite)
        );
    }

    /**
     * [CW-LOCAL-CASES] Stamps the field access of the Basis form for one Betriebsmodus.
     *
     * <h3>All four fields, every time</h3>
     * product, partner, contractType and name are stamped on EVERY call, visible ones as
     * {@code UPDATE} and hidden ones as {@code HIDDEN}. Dropping an entry does NOT un-hide the
     * field: {@code buildFormGroup} stamps {@code access} onto reused {@code MvsFormFieldDto}
     * instances and has no restore branch for it, so a field hidden once stays hidden until the
     * component is re-created. Explicit re-stamping is the proven way back.
     *
     * <h3>A forbidden field that still HAS a value stays visible</h3>
     * A hidden field keeps its form control and submits the snapshot the form loaded — hiding a
     * value therefore does not clear it. If the saved line still carries, say, a Produkt while the
     * chosen mode forbids one, hiding the field would leave the admin with a row the server refuses
     * and no control to empty. Such a field is therefore shown (and flagged in the legend) until it
     * is actually empty. Once it is, the mode's own rule takes over and it disappears.
     *
     * <h3>What this cannot do</h3>
     * {@code MvsFormControlOverwrite} carries access, label, input control and value-list filters —
     * it has NO mandatory channel, and neither has the widget layout config. The "Pflicht" half of
     * the §1.6 matrix therefore cannot be stamped from here. It belongs to the backend
     * {@code CwCaseTypeProductForm} ({@code uiMandatory} per mode), is enforced on write by
     * {@code CwCaseTypeProductValidator}, and is stated to the configurator by the legend above the
     * form. See {@link #handleProductBasicFormChange} for why the two halves must stay on opposite
     * sides of the wire.
     */
    private stampProductBasicOverwrite(mode: CwCaseTypeProductModeEnum): void {
        // Die Legende beschreibt den gewählten Modus und hängt nicht am Formular — sie wird auch
        // dann fortgeschrieben, wenn (noch) kein Bearbeitungsformular offen ist.
        this.refreshProductModeRequirements();

        if (!this.productBasicOverwrite) {
            return;
        }
        const rule = CW_PRODUCT_MODE_RULES[mode] ?? CW_PRODUCT_MODE_RULES[CwCaseTypeProductModeEnum.PRODUCT];

        this.stampField(CW_PRODUCT_FIELD_PRODUCT, rule.product, this.selectedProductProductId);
        this.stampField(CW_PRODUCT_FIELD_PARTNER, rule.partner, this.selectedProductPartnerId);
        this.stampField(CW_PRODUCT_FIELD_CONTRACT_TYPE, rule.contractType, this.selectedProductContractTypeId);

        // Die Bezeichnung wird NIE ausgeblendet: in drei von vier Modi ist sie Pflicht, im vierten
        // ist sie ein unschädlicher Anzeigetext. Sie wird trotzdem in jedem Durchlauf gestempelt,
        // damit dieser Aufruf die vollständige Feldmenge beschreibt und nicht nur die Differenz.
        this.productBasicOverwrite.addField(CW_PRODUCT_FIELD_NAME, MvsFormFieldAccessEnum.UPDATE);
    }

    /**
     * Stamps ONE classification foreign key: editable when the mode requires it, editable as well
     * when the saved line still holds a value the mode forbids (so it can be emptied), hidden only
     * when it is both forbidden and already empty.
     */
    private stampField(field: string, requiredByMode: boolean, persistedValue: number): void {
        const visible = requiredByMode || persistedValue != null;
        this.productBasicOverwrite.addField(
            field,
            visible ? MvsFormFieldAccessEnum.UPDATE : MvsFormFieldAccessEnum.HIDDEN
        );
    }

    /**
     * [CW-LOCAL-CASES] Live value snapshot of the open Basis form.
     *
     * Only a CHANGED Betriebsmodus does anything — typing in another field must not re-stamp, and
     * above all must not replace the widget, because that would re-create the form and re-fetch the
     * row from the server, throwing away everything the user had typed.
     *
     * <h3>This is the variant that SURVIVES the backend form refresh</h3>
     * With {@code mode} declared as a refresh field on the backend form
     * ({@code FormField.refreshFormOnChange}), a mode change makes the form re-fetch its TEMPLATE
     * with the values currently in it and then run
     * {@code buildFormGroup(form, dto, this.formControlOverwrite, …)} again — with the SAME
     * overwrite object this component holds. Mutating that object in place is therefore exactly what
     * the refresh picks up, and the user's entries survive. Replacing the widget instead would swap
     * an input on {@code mvs-crud-object}, which tears the form down and re-reads the row: the
     * just-picked mode would snap back to the saved one. Hence: mutate, never replace.
     *
     * <h3>Who owns which half — do not let this drift</h3>
     * {@code buildFormGroup} applies the frontend overwrite FIRST and HARD-ASSIGNS
     * {@code formField.access}, so a stamp made here OVERRIDES whatever the backend form said about
     * that field's access. The split has to stay: the backend form owns {@code uiMandatory} (the
     * Pflicht half of §1.6, which no frontend channel can express), this component owns access (the
     * hide half). The two compose correctly — {@code buildFormGroup} enforces "hidden ⇒ not
     * mandatory", so a backend mandatory on a field this mode forbids is neutralised, which is the
     * wanted outcome. If the backend form ever starts setting access per mode as well, this stamp
     * will silently win and the two authorities will diverge.
     */
    handleProductBasicFormChange(dto: DtoDetail): void {
        const mode = this.normalizeMode(dto?.['mode']);
        if (mode === this.productFormMode) {
            return;
        }
        this.productFormMode = mode;
        this.stampProductBasicOverwrite(mode);
    }

    /**
     * [CW-LOCAL-CASES] A write of the Basis form completed. The saved DTO is the new truth about the
     * line, so the persisted classification, the legend and the stamps are taken over from
     * {@code event.after}, and the read widget plus the left list are rebuilt to show it.
     *
     * <h3>The open edit form is deliberately NOT replaced here</h3>
     * The CRUD form hands its own saved DTO back into itself, so it is already showing the saved
     * state. Assigning a new {@code WidgetData} would tear the form down and build a new one — from
     * inside the very event that form is emitting — and buy nothing but an extra round trip. The
     * overwrite is instead re-stamped in place on the object the form already holds, which is what
     * makes the newly consistent row hide the fields its mode forbids on the next form build.
     *
     * A DELETE leaves no line to show, so the selection is cleared instead.
     */
    handleProductBasicChangedObject(event: ObjectChangeInformation): void {
        if (event?.action === ObjectChangeInformationActionEnum.deleted) {
            this.buildProductList();
            this.clearProductSelection();
            return;
        }

        const saved = event?.after;
        if (saved) {
            this.applySelectedProductClassificationFromDto(saved);
        } else {
            this.selectedProductMode = this.productFormMode;
        }

        // Die Liste zeigt genau EINE Spalte (e.name). Sie wird deshalb nur neu gebaut, wenn sich
        // dieser Wert geändert hat — ein Neubau setzt sonst grundlos die Zeilenmarkierung zurück,
        // während selectedProductId gesetzt bleibt (das Auswahl-Widget löst seinen Index nach einem
        // Datennachladen nicht neu auf). Modus- und Fremdschlüsselwechsel sind in der Liste
        // unsichtbar und brauchen den Neubau nicht.
        const savedName: string = saved?.['name'] ?? null;
        if (savedName !== this.selectedProductName) {
            this.selectedProductName = savedName;
            this.buildProductList();
        }

        this.productFormMode = this.selectedProductMode;
        this.stampProductBasicOverwrite(this.productFormMode);

        // Die Lesekachel wird neu gebaut — sie ist gerade NICHT gerendert (@if (!editProductBasic)),
        // es wird also keine lebende Komponente aus ihrem eigenen Ereignis heraus zerstört.
        this.productBasicDataWidget = null;
        this.buildProductBasis();
    }

    // --- Elementarprodukte drill ---
    handleProductCoverageSelect(event: ObjectIdentifierData | ObjectIdentifier): void {
        const coverageId = (event as ObjectIdentifierData)?.data?.['e.id'];
        if (!coverageId) {
            return;
        }
        this.selectedProductCoverageId = coverageId;
        this.productCoverageFieldWidget = this.typeChild(
            'cw.CwCaseTypeProductCoverageField.widget.basic.new',
            'Felder', 'cw.CwCaseTypeProductCoverageField', 'Keine Felder vorhanden', coverageId
        );
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeProductCoverageAllowance table (FK caseTypeProductCoverage) for the selected
         *   coverage component. Lives HERE as a child of the coverage and NOT as a product-level tab: an
         *   allowance row hangs off the BAUSTEIN, not off the product.
         */
        // The empty-state text carries the semantics: no row at all means "no restriction", so an empty
        // table is a valid, meaningful configuration and not a gap the user has to fill.
        this.productCoverageAllowanceWidget = this.childTable(
            'cw.CwCaseTypeProductCoverageAllowance.widget.basic.new',
            'Abhängigkeit von Tarifvariante / Basisdeckung', 'cw.CwCaseTypeProductCoverageAllowance',
            'Keine Einschränkung — der Baustein gilt für jede Tarifvariante und jede Basisdeckung.',
            'caseTypeProductCoverage', coverageId
        );
    }

    // --- [cw Vorvertrag-Erweiterung / R9] Geschäftsarten drill ---
    /**
     * A Geschäftsart row of the product line was selected: its field definitions
     * ({@code cw.CwCaseTypeProductBusinessTypeField}, FK {@code type}) open on the right, with the FK preset
     * read-only on create. These definitions are what the Vorvertrag step renders under the Geschäftsart cards —
     * only when the displayed Geschäftsart has at least one ACTIVE definition.
     */
    handleProductBusinessTypeSelect(event: ObjectIdentifierData | ObjectIdentifier): void {
        const businessTypeRowId = (event as ObjectIdentifierData)?.data?.['e.id'];
        if (!businessTypeRowId) {
            return;
        }
        const row = (event as ObjectIdentifierData)?.data;
        this.selectedProductBusinessTypeId = businessTypeRowId;
        this.selectedProductBusinessTypeValue = row?.['e.businessType'] ?? null;
        this.selectedProductBusinessTypeName = row?.['e.name'] || null;

        // Basis of the selected row: always opens in read mode; the edit form is built lazily on toggle.
        this.editProductBusinessType = false;
        this.productBusinessTypeEditWidget = null;
        this.buildProductBusinessTypeData();

        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: [R9] CwCaseTypeProductBusinessTypeField table (FK type) for the selected Geschäftsart of the
         *   product line (Produkte > Geschäftsarten > Felder).
         */
        this.productBusinessTypeFieldWidget = this.typeChild(
            'cw.CwCaseTypeProductBusinessTypeField.widget.basic.new',
            'Felder', 'cw.CwCaseTypeProductBusinessTypeField', 'Keine Felder vorhanden', businessTypeRowId
        );
    }

    /** [R9] The Geschäftsart selection was cleared — its Basis and „Felder" go with it. */
    handleProductBusinessTypeDeselect(): void {
        this.clearProductBusinessTypeSelection();
    }

    /** [R9 audit fix] Toggles the selected Geschäftsart row between read (data) and edit (object form). */
    toggleProductBusinessTypeEdit(): void {
        this.editProductBusinessType = !this.editProductBusinessType;
        if (this.editProductBusinessType && !this.productBusinessTypeEditWidget) {
            /**
             * AI: Status "in progress" | Type Widget | SubType Runtime
             * Reason: [R9 audit fix] CwCaseTypeProductBusinessType editable object form of the selected row
             *   (Produkte > Geschäftsarten > Basis, edit toggle) — keeps the row's attributes editable after creation.
             */
            this.productBusinessTypeEditWidget = WidgetFactory.createWidgetObject(
                'cw.CwCaseTypeProductBusinessType.widget.object.edit',
                'Geschäftsart bearbeiten',
                'cw.CwCaseTypeProductBusinessType',
                this.selectedProductBusinessTypeId
            );
        }
    }

    /**
     * [R9 audit fix] Save/delete round trip of the Geschäftsart edit form. A delete empties the drill and
     * reloads the list; a save rebuilds the read tile (not rendered while editing, so no live component is
     * destroyed from its own event) and relabels the list only when the Geschäftsart itself changed — a
     * rebuild would otherwise drop the row highlight for nothing.
     */
    handleProductBusinessTypeChangedObject(event: ObjectChangeInformation): void {
        if (event?.action === ObjectChangeInformationActionEnum.deleted) {
            this.clearProductBusinessTypeSelection();
            this.buildProductBusinessTypeList();
            return;
        }
        const saved = event?.after;
        if (saved) {
            this.selectedProductBusinessTypeName = saved['name'] || null;
            const savedBusinessType = saved['businessType'] ?? null;
            if (savedBusinessType !== null && String(savedBusinessType) !== String(this.selectedProductBusinessTypeValue)) {
                this.selectedProductBusinessTypeValue = savedBusinessType;
                this.buildProductBusinessTypeList();
            }
        }
        this.productBusinessTypeDataWidget = null;
        this.buildProductBusinessTypeData();
    }

    /**
     * [R9] Selectable list of the product line's Geschäftsarten, labelled by the Geschäftsart enum label
     * (`name` is optional on this entity, the Geschäftsart never is; a set `name` is shown in the detail header).
     */
    private buildProductBusinessTypeList(): void {
        this.productBusinessTypeWidget = this.productSelectable(
            'cw.CwCaseTypeProductBusinessType.widget.basic.new',
            'Geschäftsarten', 'cw.CwCaseTypeProductBusinessType', 'Keine Geschäftsarten vorhanden',
            'e.businessType'
        );
    }

    private buildProductBusinessTypeData(): void {
        if (this.productBusinessTypeDataWidget || !this.selectedProductBusinessTypeId) {
            return;
        }
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: [R9 audit fix] CwCaseTypeProductBusinessType read widget of the selected row
         *   (Produkte > Geschäftsarten > Basis).
         */
        this.productBusinessTypeDataWidget = WidgetFactory.createWidgetEntityData(
            'cw.CwCaseTypeProductBusinessType.widget.data',
            'Geschäftsart',
            'cw.CwCaseTypeProductBusinessType',
            this.selectedProductBusinessTypeId
        );
    }

    private clearProductBusinessTypeSelection(): void {
        this.selectedProductBusinessTypeId = null;
        this.selectedProductBusinessTypeValue = null;
        this.selectedProductBusinessTypeName = null;
        this.editProductBusinessType = false;
        this.productBusinessTypeDataWidget = null;
        this.productBusinessTypeEditWidget = null;
        this.productBusinessTypeFieldWidget = null;
    }

    // --- Personen drill ---
    /**
     * [CW-PERSON-REFACTOR] Person drill of a selected product line: Person → Elementarprodukte → Felder.
     *
     * <h3>Why there is no Person → Felder level any more</h3>
     * It used to build a {@code cw.CwCaseTypeProductPersonField} table here. That entity is GONE:
     * {@code cw.CwCaseTypeProductPerson} no longer implements {@code GenericTemplateType}, so it has no
     * field pool of its own and the pool that hung off it was orphaned and removed (FD-6). This is NOT
     * a lost feature — extension by configuration survives, it just moved up one level: the pool is
     * {@code cw.CwCaseTypePersonField} (FK {@code type} onto the ROLE), maintained in
     * Übersicht > Personen, and its runtime values land in {@code cw.CwCaseProductPersonField} on the
     * product-level occupancy. Defining a field once on the role beats defining it once per product
     * line. The UI hint in the template says the same thing to the configurator.
     *
     * <h3>The coverage list still needs the role id</h3>
     * {@code cw.CwCaseTypeProductPersonCoverage} has no direct FK to the ProductPerson row: its parent
     * is {@code caseTypeProductCoverage} and its person axis is the case-level {@code caseTypePerson}.
     * VERIFIED to be unchanged by the refactor — that axis did not move. What DID change is that this
     * axis and the runtime occupancy's {@code type} FK ({@code cw.CwCaseProductPerson.type}) now point
     * at the SAME entity, {@code cw.CwCaseTypePerson}; before the refactor the occupancy pointed at the
     * product/role LINK instead, so configuration and runtime named the role through two different ids.
     * Collapsing those two into one is the simplification FD-6 bought.
     */
    handleProductPersonSelect(event: ObjectIdentifierData | ObjectIdentifier): void {
        const personId = (event as ObjectIdentifierData)?.data?.['e.id'];
        if (!personId) {
            return;
        }
        this.selectedProductPersonId = personId;
        this.selectedProductPersonCaseTypePersonId = null;
        this.productPersonCoverageWidget = null;
        this.productPersonCoverageFieldWidget = null;
        this.selectedProductPersonCoverageId = null;
        this.productBeneficiaryFieldWidget = null;
        this.selectedProductBeneficiaryId = null;

        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeProductPersonAllowance table (FK caseTypeProductPerson) for the selected
         *   person line — the third leg of the allowance triad, next to the coverage and the object one.
         *   It hangs off the PERSON row and therefore lives here and not as a product-level tab.
         */
        // The FK is caseTypeProductPerson, i.e. the product-scoped person row that is selected here —
        // NOT the case-level role (caseTypePerson) the coverage list below is scoped to. Only the
        // product-scoped anchor guarantees that person, Tarifvariante and Basisdeckung belong to the
        // same product, which is exactly why the entity chose it.
        //
        // The empty-state text carries the semantics: no row at all means "no restriction", so an empty
        // table is a valid, meaningful configuration and not a gap the user has to fill. The backend
        // config checks for this entity route their findings onto this very tab.
        this.productPersonAllowanceWidget = this.childTable(
            'cw.CwCaseTypeProductPersonAllowance.widget.basic.new',
            'Abhängigkeit von Tarifvariante / Basisdeckung', 'cw.CwCaseTypeProductPersonAllowance',
            'Keine Einschränkung — die Person wird für jede Tarifvariante und jede Basisdeckung benötigt.',
            'caseTypeProductPerson', personId
        );

        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeProductPersonReqField table for the selected person line (Produkte > Personen)
         *   — which pm.Person/pm.PersonDetail attributes this role needs within THIS product, and under
         *   which alias their live value is published into the exchange model.
         */
        // FK relation is `type` (column type_id), NOT `productPerson`: this family follows the GT-field
        // quartet naming even though CwCaseTypeProductPerson is no GenericTemplateType any more. Hence
        // typeChild() and not childTable('productPerson', …) — the latter would filter on a column that
        // does not exist and QL would answer with an error, not with an empty list.
        this.productPersonReqFieldWidget = this.typeChild(
            'cw.CwCaseTypeProductPersonReqField.widget.basic.new',
            'Pflichtangaben zur Person', 'cw.CwCaseTypeProductPersonReqField',
            'Keine Pflichtangaben konfiguriert', personId
        );

        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeProductBeneficiary selectable master (FK caseTypeProductPerson) for the
         *   selected person line (Produkte > Personen > Bezugsrechte) — the entitlements that may be
         *   captured for this role in this product, each with its own field pool below.
         */
        this.productBeneficiaryWidget = this.childSelectable(
            'cw.CwCaseTypeProductBeneficiary.widget.basic.new',
            'Bezugsrechte', 'cw.CwCaseTypeProductBeneficiary', 'Keine Bezugsrechte vorhanden',
            'caseTypeProductPerson', personId
        );

        // Resolve the selected ProductPerson's caseTypePerson and scope the coverage list to that role.
        const service = this.coreService.getCrudService('cw.CwCaseTypeProductPerson');
        if (!service) {
            return;
        }
        service.list(new ObjectRequestList(false, [FilterCriteria.create('id', FilterCriteria.cOperatorEqual, personId)], [])).subscribe({
            next: (result: DtoList<CwCaseTypeProductPersonDto>) => {
                const caseTypePersonId = result?.entries?.[0]?.caseTypePersonDtoId;
                if (!caseTypePersonId) {
                    return;
                }
                this.selectedProductPersonCaseTypePersonId = caseTypePersonId;
                /**
                 * AI: Status "in progress" | Type Widget | SubType Runtime
                 * Reason: CwCaseTypeProductPersonCoverage list for the selected person (FK caseTypePerson).
                 */
                this.productPersonCoverageWidget = this.childSelectable(
                    'cw.CwCaseTypeProductPersonCoverage.widget.basic.new',
                    'Elementarprodukte', 'cw.CwCaseTypeProductPersonCoverage', 'Keine Elementarprodukte vorhanden',
                    'caseTypePerson', caseTypePersonId
                );
            }
        });
    }

    /**
     * Bezugsrecht drill: the field pool of ONE entitlement. The child's FK is {@code type}
     * (column {@code type_id}) onto the Bezugsrecht, so the GT-field helper applies unchanged.
     */
    handleProductBeneficiarySelect(event: ObjectIdentifierData | ObjectIdentifier): void {
        const beneficiaryId = (event as ObjectIdentifierData)?.data?.['e.id'];
        if (!beneficiaryId) {
            return;
        }
        this.selectedProductBeneficiaryId = beneficiaryId;
        this.productBeneficiaryFieldWidget = this.typeChild(
            'cw.CwCaseTypeProductBeneficiaryField.widget.basic.new',
            'Felder', 'cw.CwCaseTypeProductBeneficiaryField', 'Keine Felder vorhanden', beneficiaryId
        );
    }

    handleProductPersonCoverageSelect(event: ObjectIdentifierData | ObjectIdentifier): void {
        const personCoverageId = (event as ObjectIdentifierData)?.data?.['e.id'];
        if (!personCoverageId) {
            return;
        }
        this.selectedProductPersonCoverageId = personCoverageId;
        this.productPersonCoverageFieldWidget = this.typeChild(
            'cw.CwCaseTypeProductPersonCoverageField.widget.basic.new',
            'Felder', 'cw.CwCaseTypeProductPersonCoverageField', 'Keine Felder vorhanden', personCoverageId
        );
    }

    // --- Objekte drill ---
    handleProductObjectSelect(event: ObjectIdentifierData | ObjectIdentifier): void {
        const objectId = (event as ObjectIdentifierData)?.data?.['e.id'];
        if (!objectId) {
            return;
        }
        this.selectedProductObjectId = objectId;
        this.productObjectCoverageWidget = null;
        this.productObjectCoverageFieldWidget = null;
        this.selectedProductObjectCoverageId = null;
        this.productObjectFieldIotFieldWidget = null;
        this.selectedProductObjectFieldId = null;
        this.productObjectDefinitionIotFieldWidget = null;

        this.productObjectFieldWidget = this.typeChild(
            'cw.CwCaseTypeProductObjectField.widget.basic.new',
            'Felder', 'cw.CwCaseTypeProductObjectField', 'Keine Felder vorhanden', objectId
        );
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeProductObjectAllowance table (FK caseTypeProductObject) for the selected object
         *   line — the object leg of the allowance triad, structurally identical to the coverage and the
         *   person one. An allowance row hangs off the OBJEKT, not off the product.
         */
        // The empty-state text carries the semantics: no row at all means "no restriction", so an empty
        // table is a valid, meaningful configuration and not a gap the user has to fill.
        this.productObjectAllowanceWidget = this.childTable(
            'cw.CwCaseTypeProductObjectAllowance.widget.basic.new',
            'Abhängigkeit von Tarifvariante / Basisdeckung', 'cw.CwCaseTypeProductObjectAllowance',
            'Keine Einschränkung — das Objekt wird für jede Tarifvariante und jede Basisdeckung benötigt.',
            'caseTypeProductObject', objectId
        );
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: [CW-IOT-DUAL-ANCHOR] CwCaseTypeProductObjectFieldIotField table for the DEFINITION row
         *   kind (FK caseTypeProductObject) of the selected object — the mappings that occupy no product
         *   field but declare an insurable-object-type field as needed and publish it under io_<alias>.
         */
        // TWO tables for ONE entity, because it has TWO owner FKs and an explicit discriminator:
        //   DEFINITION   → caseTypeProductObject set, caseTypeProductObjectField null (THIS table)
        //   TARGET_FIELD → the reverse; see handleProductObjectFieldSelect()
        // The create preset therefore stamps mappingKind = DEFINITION explicitly. Without it the ENTITY
        // DEFAULT (TARGET_FIELD) would win and produce a self-contradictory row — discriminator says
        // "target field", populated owner FK is the object.
        //
        // The FILTER, by contrast, runs over the FK and deliberately NOT over mappingKind: V390 added
        // mapping_kind as a bare nullable SMALLINT with no DEFAULT and no back-fill, so legacy rows on
        // clone and prod hold NULL. A discriminator filter would make exactly those rows vanish from the
        // screen — silently, since a NULL is UNKNOWN and matches neither side.
        //
        // mappingBehaviour is preset as well, and that is NOT decoration: the column is @NotNull with no
        // Java default, and validation rule V2' demands DEFAULT on a DEFINITION row (there is no cw field
        // to bind back to, so BIND describes a flow that cannot exist). The backend form DOES hide the
        // control for such a row — but only on the REGULAR path, which needs a persisted entity; the CREATE
        // dialog resolves through postNoneRegularFormGenerate with a null entity, so it still renders the
        // field. Without the preset the configurator meets a mandatory choice whose second option is
        // guaranteed to be rejected on save.
        this.productObjectDefinitionIotFieldWidget = this.childTable(
            'cw.CwCaseTypeProductObjectFieldIotField.widget.definition.new',
            'Definitions-Zuordnungen', 'cw.CwCaseTypeProductObjectFieldIotField',
            'Keine Definitions-Zuordnung vorhanden.',
            'caseTypeProductObject', objectId,
            [
                {field: 'mappingKind', value: CwFieldMappingKindEnum.DEFINITION},
                {field: 'mappingBehaviour', value: CwFieldMappingBehaviourEnum.DEFAULT},
            ]
        );
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeProductObjectCoverage list for the selected object (FK caseTypeProductObject).
         */
        this.productObjectCoverageWidget = this.childSelectable(
            'cw.CwCaseTypeProductObjectCoverage.widget.basic.new',
            'Elementarprodukte', 'cw.CwCaseTypeProductObjectCoverage', 'Keine Elementarprodukte vorhanden',
            'caseTypeProductObject', objectId
        );
    }

    /**
     * Objektfeld drill: the {@code ci.InsurableObjectType} source-field mappings of ONE object field row.
     *
     * <h3>Only the TARGET_FIELD half of a dual-anchor entity is reachable here</h3>
     * {@code cw.CwCaseTypeProductObjectFieldIotField} carries an explicit discriminator
     * {@code mappingKind} and TWO nullable owner FKs, exactly one of which is populated per row:
     * {@code TARGET_FIELD} rows hang on {@code caseTypeProductObjectField} (the field selected here),
     * {@code DEFINITION} rows hang on {@code caseTypeProductObject} directly and map no cw field at all.
     * This table is scoped to the FIELD, so it shows — and creates — TARGET_FIELD rows only; the DEFINITION
     * half has its own object-scoped table, built in {@link #handleProductObjectSelect}. Each table stamps
     * its own {@code mappingKind} on create, READ-locked, so the table a row is created in is what fixes
     * its kind. Neither table FILTERS on the discriminator — see the note at the DEFINITION table for why
     * that would hide legacy rows.
     */
    handleProductObjectFieldSelect(event: ObjectIdentifierData | ObjectIdentifier): void {
        const objectFieldId = (event as ObjectIdentifierData)?.data?.['e.id'];
        if (!objectFieldId) {
            return;
        }
        this.selectedProductObjectFieldId = objectFieldId;
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeProductObjectFieldIotField table (FK caseTypeProductObjectField) for the
         *   selected object field — which ci.InsurableObjectTypeField pre-fills this cw field, per
         *   concrete ci.InsurableObjectType.
         */
        // mappingKind is stamped EXPLICITLY even though TARGET_FIELD is the entity default — a row must
        // state what it is, and relying on a default here would make the two tables disagree about how
        // deliberate their discriminator is. The filter stays on the FK, never on mappingKind: legacy rows
        // hold NULL in that column (V390 shipped it nullable, with no DEFAULT and no back-fill), and a
        // discriminator filter would hide exactly them.
        this.productObjectFieldIotFieldWidget = this.childTable(
            'cw.CwCaseTypeProductObjectFieldIotField.widget.basic.new',
            'Zielfeld-Zuordnungen', 'cw.CwCaseTypeProductObjectFieldIotField',
            'Keine Zuordnung vorhanden — das Feld wird nicht aus dem Versicherungsobjekt vorbelegt.',
            'caseTypeProductObjectField', objectFieldId,
            [{field: 'mappingKind', value: CwFieldMappingKindEnum.TARGET_FIELD}]
        );
    }

    handleProductObjectCoverageSelect(event: ObjectIdentifierData | ObjectIdentifier): void {
        const objectCoverageId = (event as ObjectIdentifierData)?.data?.['e.id'];
        if (!objectCoverageId) {
            return;
        }
        this.selectedProductObjectCoverageId = objectCoverageId;
        this.productObjectCoverageFieldWidget = this.typeChild(
            'cw.CwCaseTypeProductObjectCoverageField.widget.basic.new',
            'Felder', 'cw.CwCaseTypeProductObjectCoverageField', 'Keine Felder vorhanden', objectCoverageId
        );
    }

    // --- Fragen drill ---
    handleProductQuestionSectionSelect(event: ObjectIdentifierData | ObjectIdentifier): void {
        const sectionId = (event as ObjectIdentifierData)?.data?.['e.id'];
        if (!sectionId) {
            return;
        }
        this.selectedProductQuestionSectionId = sectionId;
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeProductQuestion table (FK section) for the selected product question section.
         */
        // FK relation 'section' — the same name as on the case side; NOT 'type' (this family is not a
        // GT field quartet) and NOT 'questionGroup' (that relation no longer exists).
        this.productQuestionWidget = this.childTable(
            'cw.CwCaseTypeProductQuestion.widget.basic.new',
            'Fragen', 'cw.CwCaseTypeProductQuestion', 'Keine Fragen vorhanden',
            'section', sectionId
        );
    }

    // ============================ Widget helpers ============================

    /**
     * Generic table-widget builder shared by every FK-`caseTypeProduct`-scoped product tab.
     *
     * @param sorting optional order of the rows; omitted by the tabs whose rows carry no order field.
     */
    private productTable(alias: string, name: string, entity: string, noData: string, sorting?: Sorting): WidgetData {
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: Product-scoped list table (FK caseTypeProduct) for a Produkte tab; the entity/alias are supplied by the calling product-tab builder.
         */
        const widget = WidgetFactory.createWidgetTableEntityQl(
            alias, name, entity, noData,
            [FilterCriteria.create('e.caseTypeProductId', FilterCriteria.cOperatorEqual, this.selectedProductId)],
            sorting ? [sorting] : [],
            // resolveFkeyTexts: render foreign keys as their display text instead of the raw id.
            true
        );
        widget.functionCallbacks = cwWidgetToolbarCreate([{field: 'caseTypeProductDtoId', value: this.selectedProductId}]);
        return widget;
    }

    /**
     * Generic selectable-master builder shared by every FK-`caseTypeProduct`-scoped product tab.
     *
     * @param attributeToDisplay the row label; {@code e.name} unless the entity's name is optional
     */
    private productSelectable(alias: string, name: string, entity: string, noData: string,
                              attributeToDisplay: string = 'e.name'): WidgetData {
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: Product-scoped selectable master (FK caseTypeProduct) for a Produkte parent list; the entity/alias are supplied by the calling product-tab builder.
         */
        const widget = WidgetFactory.createWidgetSelectableEntityQl(
            alias, name, entity, noData,
            [FilterCriteria.create('e.caseTypeProductId', FilterCriteria.cOperatorEqual, this.selectedProductId)],
            [],
            WidgetDataParam.create('attributeToDisplay', attributeToDisplay)
        );
        widget.functionCallbacks = cwWidgetToolbarCreate([{field: 'caseTypeProductDtoId', value: this.selectedProductId}]);
        return widget;
    }

    /** Generic GT-field child-table builder (FK `type`) shared by the product field tabs. */
    private typeChild(alias: string, name: string, entity: string, noData: string, parentId: number): WidgetData {
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: GT-field child table (FK type) for a selected product parent; the entity/alias are supplied by the calling selection handler.
         */
        const widget = WidgetFactory.createWidgetTableEntityQl(
            alias, name, entity, noData,
            [FilterCriteria.create('e.typeId', FilterCriteria.cOperatorEqual, parentId)],
            [],
            // resolveFkeyTexts: render foreign keys as their display text instead of the raw id.
            true
        );
        widget.functionCallbacks = cwWidgetToolbarCreate([{field: 'typeDtoId', value: parentId}]);
        return widget;
    }

    /**
     * Generic child TABLE builder (arbitrary FK relation) with a matching create preset.
     *
     * @param extraPresets further values to preset READ-only on the create form, beyond the parent FK.
     *   Used where the child carries a DISCRIMINATOR the table itself decides — see the two
     *   {@code mappingKind} tables in {@link #handleProductObjectSelect} and
     *   {@link #handleProductObjectFieldSelect}. The presets are locked to READ by
     *   {@code cwWidgetToolbarCreate}, which is exactly right for a discriminator: the table the row is
     *   created in is what fixes its kind, so it must not be editable there.
     */
    private childTable(alias: string, name: string, entity: string, noData: string, fkRelation: string,
                       parentId: number, extraPresets: CwFkPreset[] = []): WidgetData {
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: Child list table scoped to a selected parent (FK <fkRelation>) for a Produkte master-detail tab; the entity/alias/relation are supplied by the calling selection handler.
         */
        const widget = WidgetFactory.createWidgetTableEntityQl(
            alias, name, entity, noData,
            [FilterCriteria.create(`e.${fkRelation}Id`, FilterCriteria.cOperatorEqual, parentId)],
            [],
            // resolveFkeyTexts: render foreign keys as their display text instead of the raw id.
            true
        );
        widget.functionCallbacks = cwWidgetToolbarCreate(
            [{field: `${fkRelation}DtoId`, value: parentId}, ...extraPresets]
        );
        return widget;
    }

    /** Generic selectable-child builder (arbitrary FK relation) shared by the product drill tabs. */
    private childSelectable(alias: string, name: string, entity: string, noData: string, fkRelation: string, parentId: number): WidgetData {
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: Selectable child (FK <fkRelation>) scoped to a selected product parent; the entity/alias/relation are supplied by the calling selection handler.
         */
        const widget = WidgetFactory.createWidgetSelectableEntityQl(
            alias, name, entity, noData,
            [FilterCriteria.create(`e.${fkRelation}Id`, FilterCriteria.cOperatorEqual, parentId)],
            [],
            WidgetDataParam.create('attributeToDisplay', 'e.name')
        );
        widget.functionCallbacks = cwWidgetToolbarCreate([{field: `${fkRelation}DtoId`, value: parentId}]);
        return widget;
    }

    private clearProductSelection(): void {
        this.selectedProductId = null;
        this.productActiveTab = 'basis';
        this.productBasicDataWidget = null;
        this.productBasicEditWidget = null;
        this.editProductBasic = false;
        // [CW-LOCAL-CASES] Der Betriebsmodus und die drei Klassifikationsfelder gehören zur ZEILE —
        // sie müssen mit der Auswahl fallen, sonst stempelt die nächste Zeile mit fremden Werten.
        this.productBasicOverwrite = null;
        this.selectedProductMode = CwCaseTypeProductModeEnum.PRODUCT;
        this.productFormMode = CwCaseTypeProductModeEnum.PRODUCT;
        this.selectedProductProductId = null;
        this.selectedProductPartnerId = null;
        this.selectedProductContractTypeId = null;
        this.selectedProductProductName = null;
        this.selectedProductPartnerName = null;
        this.selectedProductContractTypeName = null;
        this.selectedProductName = null;
        this.productModeRequirements = [];
        this.productBiproWidget = null;
        this.productFieldWidget = null;
        this.productTariffWidget = null;
        this.productBaseCoverageWidget = null;
        this.productDurationWidget = null;
        this.productCoverageWidget = null;
        this.productCoverageFieldWidget = null;
        this.productCoverageAllowanceWidget = null;
        this.selectedProductCoverageId = null;
        this.productPersonWidget = null;
        this.productPersonCoverageWidget = null;
        this.productPersonCoverageFieldWidget = null;
        this.selectedProductPersonId = null;
        this.selectedProductPersonCaseTypePersonId = null;
        this.selectedProductPersonCoverageId = null;
        this.productPersonAllowanceWidget = null;
        this.productPersonReqFieldWidget = null;
        this.productBeneficiaryWidget = null;
        this.productBeneficiaryFieldWidget = null;
        this.selectedProductBeneficiaryId = null;
        this.productObjectWidget = null;
        this.productObjectFieldWidget = null;
        this.productObjectCoverageWidget = null;
        this.productObjectCoverageFieldWidget = null;
        this.selectedProductObjectId = null;
        this.selectedProductObjectCoverageId = null;
        this.productObjectAllowanceWidget = null;
        this.productObjectFieldIotFieldWidget = null;
        this.selectedProductObjectFieldId = null;
        this.productObjectDefinitionIotFieldWidget = null;
        this.productQuestionSectionWidget = null;
        this.productQuestionWidget = null;
        this.selectedProductQuestionSectionId = null;
        this.productFormerContractTypeWidget = null;
        this.productFormerProductWidget = null;
        this.productBusinessTypeWidget = null;
        this.clearProductBusinessTypeSelection();
        this.productPartnerRestrictWidget = null;
    }
}
